import { DogSchool } from "@/data/dogSchools";

/**
 * Sort schools by review count
 * @param schools List of dog schools
 * @returns Sorted list of dog schools
 */
export const sortSchoolsByReviewCount = (schools: DogSchool[]): DogSchool[] => {
  return [...schools].sort((a, b) => (b.reviewCount || 0) - (a.reviewCount || 0));
};

/**
 * Sort schools by rating
 * @param schools List of dog schools
 * @param ascending Sort in ascending order (lowest first)
 * @returns Sorted list of dog schools
 */
export const sortSchoolsByRating = (schools: DogSchool[], ascending: boolean = false): DogSchool[] => {
  return [...schools].sort((a, b) => {
    const ratingA = a.googleReview || 0;
    const ratingB = b.googleReview || 0;
    return ascending ? ratingA - ratingB : ratingB - ratingA;
  });
};

/**
 * Sort schools alphabetically
 * @param schools List of dog schools
 * @param ascending Sort in ascending order (A-Z)
 * @returns Sorted list of dog schools
 */
export const sortSchoolsAlphabetically = (schools: DogSchool[], ascending: boolean = true): DogSchool[] => {
  return [...schools].sort((a, b) => {
    const nameA = a.name || '';
    const nameB = b.name || '';
    return ascending ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
  });
};

/**
 * Sort schools by distance (must have distance property set)
 * @param schools List of dog schools with distance property
 * @param ascending Sort in ascending order (closest first)
 * @returns Sorted list of dog schools
 */
export const sortSchoolsByDistance = (schools: DogSchool[], ascending: boolean = true): DogSchool[] => {
  return [...schools].sort((a, b) => {
    const distanceA = (a as any).distance !== undefined ? (a as any).distance : Number.MAX_VALUE;
    const distanceB = (b as any).distance !== undefined ? (b as any).distance : Number.MAX_VALUE;
    return ascending ? distanceA - distanceB : distanceB - distanceA;
  });
};

/**
 * Sort schools by proximity to a postal code (using a simplified distance calculation)
 * @param schools List of dog schools
 * @param userPostalCode User's postal code
 * @param ascending Sort in ascending order (closest first)
 * @returns Sorted list of dog schools
 */
export const sortSchoolsByProximity = (schools: DogSchool[], userPostalCode: string, ascending: boolean = true): DogSchool[] => {
  // This is a placeholder - replace with actual distance calculation logic
  return [...schools].sort(() => (ascending ? 1 : -1));
};

/**
 * Calculate distance between two geographic coordinates using Haversine formula
 * @param lat1 Latitude of point 1
 * @param lng1 Longitude of point 1
 * @param lat2 Latitude of point 2
 * @param lng2 Longitude of point 2
 * @returns Distance in kilometers
 */
export const calculateHaversineDistance = (
  lat1: number, 
  lng1: number, 
  lat2: number, 
  lng2: number
): number => {
  // Convert lat/lng from degrees to radians
  const toRad = (value: number) => value * Math.PI / 180;
  const rlat1 = toRad(lat1);
  const rlng1 = toRad(lng1);
  const rlat2 = toRad(lat2);
  const rlng2 = toRad(lng2);
  
  // Haversine formula
  const R = 6371; // Radius of the earth in km
  const dLat = rlat2 - rlat1;
  const dLon = rlng2 - rlng1;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(rlat1) * Math.cos(rlat2) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
  const distance = R * c; // Distance in km
  
  return distance;
};

/**
 * Filter schools strictly by distance
 * @param schools List of dog schools
 * @param centerLat Center latitude
 * @param centerLng Center longitude
 * @param radiusKm Maximum radius in kilometers
 * @returns Filtered list of dog schools
 */
export const filterSchoolsByDistanceStrict = (
  schools: DogSchool[],
  centerLat: number,
  centerLng: number,
  radiusKm: number
): DogSchool[] => {
  console.log(`Strictly filtering ${schools.length} schools within ${radiusKm}km of [${centerLat}, ${centerLng}]`);
  
  return schools.filter(school => {
    // Skip schools without coordinates
    if (school.latitude === undefined || school.longitude === undefined) {
      console.log(`School missing coordinates: ${school.name}`);
      return false;
    }
    
    // Calculate distance using Haversine formula
    const distance = calculateHaversineDistance(
      centerLat, 
      centerLng, 
      school.latitude, 
      school.longitude
    );
    
    // Add distance property to school for sorting later
    (school as any).distance = distance;
    
    const withinRadius = distance <= radiusKm;
    
    if (withinRadius) {
      console.log(`School ${school.name} is within radius: ${distance.toFixed(2)}km of ${radiusKm}km`);
    } else {
      console.log(`School ${school.name} is outside radius: ${distance.toFixed(2)}km > ${radiusKm}km`);
    }
    
    return withinRadius;
  });
};

/**
 * Sort schools by proximity to coordinates
 * @param schools List of dog schools
 * @param lat Latitude
 * @param lng Longitude
 * @param ascending Sort in ascending order (closest first)
 * @returns Sorted list of dog schools
 */
export const sortSchoolsByProximityToCoordinates = (
  schools: DogSchool[],
  lat: number,
  lng: number,
  ascending: boolean = true
): DogSchool[] => {
  // Calculate distances for each school
  const schoolsWithDistance = schools.map(school => {
    let distance = Number.MAX_VALUE;
    
    // Skip distance calculation if school has no coordinates
    if (school.latitude !== undefined && school.longitude !== undefined) {
      distance = calculateHaversineDistance(
        lat, 
        lng, 
        school.latitude, 
        school.longitude
      );
      
      // Add distance property to school for reference
      (school as any).distance = distance;
    }
    
    return { school, distance };
  });
  
  // Sort by distance
  schoolsWithDistance.sort((a, b) => 
    ascending ? a.distance - b.distance : b.distance - a.distance
  );
  
  // Return just the schools in sorted order
  return schoolsWithDistance.map(item => item.school);
};
